import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useClientAuth } from '@/hooks/useClientAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SEOHead } from '@/components/seo/SEOHead';
import { useToast } from '@/hooks/use-toast';
import '../styles/client-theme.css';

export default function EspaceClient() {
  const navigate = useNavigate();
  const { user, signInWithEmail, signUp } = useAuth();
  const { clients, loading: clientLoading, refetch } = useClientAuth();
  const { toast } = useToast();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);

  // Gestion des redirections automatiques
  useEffect(() => {
    if (user && !clientLoading) {
      if (clients.length === 0) {
        createKaraskinParisDemo();
      } else if (clients.length === 1) {
        navigate(`/dashboard/${clients[0].slug}`);
      }
    }
  }, [user, clients, clientLoading]);

  const createKaraskinParisDemo = async () => {
    try {
      // Utiliser la fonction RPC idempotente qui gère tous les cas
      const { data: clientId, error } = await supabase
        .rpc('create_karaskin_demo_client', {
          user_id_param: user!.id
        });

      if (error) throw error;

      // Recharger les clients et rediriger
      await refetch();
      navigate('/dashboard/karaskin-paris');
    } catch (error) {
      console.error('Error creating demo client:', error);
      toast({
        title: "Erreur",
        description: "Impossible de créer l'accès au client démo",
        variant: "destructive"
      });
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setLoading(true);
    try {
      const { error } = isSignUp 
        ? await signUp(email, password)
        : await signInWithEmail(email, password);

      if (error) {
        toast({
          title: "Erreur d'authentification",
          description: error.message,
          variant: "destructive"
        });
      } else if (isSignUp) {
        toast({
          title: "Compte créé",
          description: "Vérifiez votre email pour activer votre compte",
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Auth error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Formulaire d'authentification si pas connecté
  if (!user) {
    return (
      <div className="client-scope">
        <SEOHead 
          title="Connexion Espace Client"
          description="Accédez à votre tableau de bord Amazon. Connexion sécurisée."
        />
        
        <div className="flex min-h-screen items-center justify-center">
          <Card className="auth-form w-[400px]">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-center">
                {isSignUp ? 'Créer un compte' : 'Connexion Espace Client'}
              </CardTitle>
              <CardDescription className="text-center">
                {isSignUp 
                  ? 'Créez votre compte pour accéder au dashboard'
                  : 'Connectez-vous pour accéder à votre dashboard'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAuth} className="space-y-4">
                <div>
                  <Input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="auth-input"
                    required
                  />
                </div>
                <div>
                  <Input
                    type="password"
                    placeholder="Mot de passe"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="auth-input"
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  className="btn-accent w-full"
                  disabled={loading}
                >
                  {loading ? 'Connexion...' : (isSignUp ? 'Créer le compte' : 'Se connecter')}
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setIsSignUp(!isSignUp)}
                    className="text-accent text-sm hover:underline"
                  >
                    {isSignUp 
                      ? 'Déjà un compte ? Se connecter'
                      : 'Pas de compte ? Créer un compte'
                    }
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Chargement des clients
  if (clientLoading) {
    return (
      <div className="client-scope">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="skeleton w-8 h-8 mx-auto mb-4"></div>
            <p className="text-muted">Chargement de vos clients...</p>
          </div>
        </div>
      </div>
    );
  }

  // Sélecteur multi-clients (si plus de 1 client)
  if (clients.length > 1) {
    return (
      <div className="client-scope">
        <SEOHead 
          title="Espace Client - Sélection"
          description="Choisissez le client à gérer dans votre tableau de bord Amazon."
        />
        
        <div className="min-h-screen flex flex-col items-center justify-center p-6">
          <h2 className="text-2xl font-bold mb-8 text-center">Choisissez un client</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl w-full">
            {clients.map((client) => (
              <Card 
                key={client.id} 
                className="card hover:scale-105 transition-transform cursor-pointer"
                onClick={() => navigate(`/dashboard/${client.slug}`)}
              >
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    {client.brand_logo_url && (
                      <img 
                        src={client.brand_logo_url} 
                        alt={client.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    )}
                    <div>
                      <CardTitle className="text-lg">{client.name}</CardTitle>
                      <CardDescription className="text-muted">
                        {Array.isArray(client.marketplaces) 
                          ? client.marketplaces.join(', ') 
                          : client.marketplace
                        }
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Button className="btn-accent w-full">
                    Accéder au dashboard
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // État de chargement pendant création/redirection
  return (
    <div className="client-scope">
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="skeleton w-8 h-8 mx-auto mb-4"></div>
          <p className="text-muted">Initialisation de votre espace client...</p>
        </div>
      </div>
    </div>
  );
}